import { useEffect, useId, useRef, useState } from 'react';
import {
  LIBELLES_MOTIF_ECART,
  MOTIFS_ECART,
  MOTIF_LIBRE_MAX,
  transitionsPossibles,
  type CandidatureDetail,
  type ChangementStatut,
  type MotifEcart,
  type StatutCandidature,
} from '@rh/shared';
import { appel } from '../api';
import { Erreur } from './Etat';

/**
 * Bouton « Écarter » et sa fenêtre de motifs (demande du 2026-09-19). Le motif
 * est obligatoire, comme avant, mais il se coche au lieu de se taper : les
 * dossiers écartés deviennent comparables d'une annonce à l'autre.
 *
 * La liste proposée ne comporte NI âge, NI sexe, NI situation familiale, NI
 * santé (CLAUDE.md § Interdits) : ce sont des critères discriminatoires, que
 * l'application ne stocke pas et n'écrira pas dans un dossier. Ce qui relève
 * de la disponibilité ou de l'expérience se coche ; le reste se rédige dans
 * « Autre », sous la responsabilité de son auteur.
 */
export function Ecarter({
  candidatureId,
  statut,
  onChange,
}: {
  candidatureId: string;
  statut: StatutCandidature;
  onChange: (detail: CandidatureDetail) => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [motifs, setMotifs] = useState<MotifEcart[]>([]);
  const [autre, setAutre] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const bouton = useRef<HTMLButtonElement>(null);
  const premier = useRef<HTMLInputElement>(null);
  const titre = useId();

  const possible = transitionsPossibles(statut).includes('ecarte');

  useEffect(() => {
    if (!ouvert) return;
    premier.current?.focus();
    const clavier = (evenement: KeyboardEvent) => {
      if (evenement.key !== 'Escape' || envoi) return;
      setOuvert(false);
      bouton.current?.focus();
    };
    document.addEventListener('keydown', clavier);
    return () => document.removeEventListener('keydown', clavier);
  }, [ouvert, envoi]);

  if (!possible) return null;

  function fermer(): void {
    setOuvert(false);
    setErreur(null);
    bouton.current?.focus();
  }

  function basculer(motif: MotifEcart): void {
    setMotifs((liste) => (liste.includes(motif) ? liste.filter((m) => m !== motif) : [...liste, motif]));
  }

  const rien = motifs.length === 0 && !autre.trim();

  async function valider(): Promise<void> {
    if (rien) return;
    setErreur(null);
    setEnvoi(true);
    try {
      const corps: ChangementStatut = { statut: 'ecarte', motifs, motif: autre.trim() || null };
      const detail = await appel<CandidatureDetail>(`/candidatures/${candidatureId}`, {
        method: 'PATCH',
        body: JSON.stringify(corps),
      });
      setMotifs([]);
      setAutre('');
      setOuvert(false);
      onChange(detail);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Écart impossible');
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <>
      <button
        ref={bouton}
        type="button"
        className="bouton-second bouton-danger"
        onClick={() => setOuvert(true)}
      >
        Écarter
      </button>
      {ouvert && (
        <div className="dialogue-fond">
          <dialog open className="dialogue dialogue-ecarter" aria-modal="true" aria-labelledby={titre}>
            <h2 id={titre}>Pourquoi écarter ce candidat ?</h2>
            <p className="mention">
              Le motif reste dans le dossier et ressort dans les exports. Cochez ce qui s’applique.
            </p>
            <ul className="motifs-ecart">
              {MOTIFS_ECART.map((motif, rang) => (
                <li key={motif}>
                  <label className="case">
                    <input
                      ref={rang === 0 ? premier : undefined}
                      type="checkbox"
                      checked={motifs.includes(motif)}
                      onChange={() => basculer(motif)}
                    />
                    <span>{LIBELLES_MOTIF_ECART[motif]}</span>
                  </label>
                </li>
              ))}
            </ul>
            <label className="champ">
              <span>Autre motif</span>
              <textarea
                rows={2}
                maxLength={MOTIF_LIBRE_MAX}
                value={autre}
                placeholder="À écrire seulement si les cases ci-dessus ne disent pas tout"
                onChange={(evenement) => setAutre(evenement.target.value)}
              />
            </label>
            <p className="mention">
              Ni l’âge, ni le sexe, ni la situation familiale, ni la santé ne sont des motifs
              recevables : l’application ne les enregistre pas.
            </p>
            {erreur && <Erreur message={erreur} />}
            <div className="actions">
              <button type="button" className="bouton-second" disabled={envoi} onClick={fermer}>
                Annuler
              </button>
              <button
                type="button"
                className="bouton bouton-danger"
                disabled={envoi || rien}
                onClick={() => void valider()}
              >
                {envoi ? 'Enregistrement…' : 'Écarter le candidat'}
              </button>
            </div>
          </dialog>
        </div>
      )}
    </>
  );
}
